// =============================================================================
// Tiny haptic feedback helper.
//
// Wraps `navigator.vibrate` (Android Chrome / Firefox / installed PWAs) so we
// don't have to feature-check at every call site. iOS Safari has historically
// ignored `navigator.vibrate`, but newer iOS versions support it inside an
// installed standalone PWA — so calling these freely is safe (no-op if the
// platform doesn't support it) and provides a premium tactile touch wherever
// it does.
//
// Usage:
//   import { haptics } from '../lib/haptics';
//   haptics.tap();         // 8ms — for a tap, button press, check-off
//   haptics.impact();      // 15ms — start/stop a timer, swipe-to-delete
//   haptics.success();     // light double-tap pattern
//   haptics.warning();     // longer single buzz for destructive confirms
//
// Pass an explicit number/array to override:
//   haptics.vibrate(20);
//   haptics.vibrate([10, 50, 10]);
// =============================================================================

const isSupported =
  typeof navigator !== 'undefined' &&
  typeof navigator.vibrate === 'function';

const vibrate = (pattern) => {
  if (!isSupported) return false;
  try {
    return navigator.vibrate(pattern);
  } catch {
    return false;
  }
};

export const haptics = {
  isSupported,
  vibrate,
  // Subtle "I heard you" — list item check-off, toggle, tap.
  tap: () => vibrate(8),
  // Slightly meatier — start/stop a timer, swipe-to-delete commit, long-press.
  impact: () => vibrate(15),
  // Two short pulses — successful save / sync / join.
  success: () => vibrate([10, 40, 10]),
  // Single longer buzz — destructive action confirmed, error.
  warning: () => vibrate(30)
};

export default haptics;
